import { useAui } from "@assistant-ui/react";
import { Box, Flex, Text } from "@chakra-ui/react";
import React, { FC } from "react";

import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import { buildFiltersMessageForAssistant } from "./customInsightsChatUtils";
import CustomInsightsFilterAndPreview from "./CustomInsightsFilterAndPreview";
import type { FilterState } from "./CustomInsightsFiltersProvider";
import { useCustomInsightsFiltersContext } from "./CustomInsightsFiltersProvider";
import { InsightsChatInner } from "./InsightsChatLayout";

function ChatSummaryBox(): React.ReactElement {
  const { hasPreviewInterviews, isPreviewLoading } = useCustomInsightsChat();

  return (
    <Flex direction="row" alignItems="center" gap="20px" px="32px" py="16px">
      {isPreviewLoading ? (
        <Text
          fontSize="14px"
          lineHeight="20px"
          color="gray.500"
          textAlign="center"
          width="100%"
        >
          Checking matching interviews...
        </Text>
      ) : !hasPreviewInterviews ? (
        <Text
          fontSize="14px"
          lineHeight="20px"
          color="red.600"
          textAlign="center"
          width="100%"
        >
          No matching interviews for the current filters. Update them to enable
          chat.
        </Text>
      ) : null}
    </Flex>
  );
}

export default function CustomInsightsChatPage() {
  const { filtersOpen } = useCustomInsightsChat();

  return (
    <Box bg="white" flex="1" minWidth="0" overflow="auto" h="100%">
      <Flex
        flexDir="column"
        mx="auto"
        boxSizing="initial"
        h="100%"
        minH="0"
        flex={1}
        minW={0}
      >
        <Box
          flexShrink={filtersOpen ? 1 : 0}
          flex={filtersOpen ? 1 : undefined}
          minH={filtersOpen ? 0 : undefined}
        >
          <InsightsChatFilters />
        </Box>
        {!filtersOpen && (
          <Flex flex={1} minH={0} flexDir="column" bg="rgba(77, 55, 222, 0.04)">
            <ChatSummaryBox />
            <Flex flex={1} minH={0} position="relative" px="4">
              <InsightsChatInner filtersOpen={filtersOpen} />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Box>
  );
}

const InsightsChatFilters: FC = () => {
  const aui = useAui();
  const { addFilterSnapshot, filtersOpen, setFiltersOpen } =
    useCustomInsightsChat();
  const filters = useCustomInsightsFiltersContext();

  const currentFilterState: FilterState = {
    callNameSearch: filters.callNameSearch,
    dateRange: filters.dateRange,
    selectedPositions: filters.selectedPositions,
    selectedInterviewers: filters.selectedInterviewers,
    selectedDepartments: filters.selectedDepartments,
    selectedStages: filters.selectedStages,
    selectedLocations: filters.selectedLocations,
    selectedCandidateOutcomes: filters.selectedCandidateOutcomes,
    durationRange: filters.durationRange,
    interviewType: filters.interviewType,
    maxInterviewCount: filters.maxInterviewCount,
  };

  const onApplyFilters = (): void => {
    addFilterSnapshot(currentFilterState);
    aui.thread().append({
      role: "user",
      content: [
        {
          type: "text",
          text: buildFiltersMessageForAssistant(currentFilterState),
        },
      ],
    });
  };

  return (
    <CustomInsightsFilterAndPreview
      onApply={onApplyFilters}
      isOpen={filtersOpen}
      onOpenChange={setFiltersOpen}
    />
  );
};
